import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";
import { euro, euroDezimal } from "@/utils/preisAnalyse";

/**
 * Der firmenspezifische Teil.
 *
 * Aus der Search Console: Die Weihnachtsfeier ist mit Abstand der größte
 * Unterbegriff des Clusters (rund 1.100 Impressionen), dazu Fragen wie "was
 * ist die beste fotobox für eine firmenfeier mit 100 mitarbeitern" und
 * "vergleich: fotobox-verleih mit digitaler bildergalerie für firmenkunden".
 *
 * Zwei Themen kommen hier vor, die es privat nicht gibt: das Logo im
 * Drucklayout und die Frage, ob Mitarbeiterfotos überhaupt verwendet werden
 * dürfen. Beides steht auf keiner Anbieterseite — und beides entscheidet,
 * ob eine Firma bucht.
 */

function Abschnitt({ id, titel, untertitel, children }) {
  return (
    <SectionHeader id={id} title={titel} subtitle={untertitel}>
      <div className="mx-auto mt-8 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        {children}
      </div>
    </SectionHeader>
  );
}

export default function FirmenfeierInhalt({ analyse, stand }) {
  if (!analyse) return null;

  return (
    <>
      <Abschnitt
        id="kosten"
        titel="Was kostet eine Fotobox für die Firmenfeier?"
        untertitel={`Gerechnet für ${analyse.gaeste} Mitarbeitende — inklusive Ausdrucken für alle.`}
      >
        <p>
          Bei{" "}
          <strong className="font-semibold text-slate-900">
            {analyse.gaeste} Gästen
          </strong>{" "}
          solltet ihr mit{" "}
          <strong className="font-semibold text-slate-900">
            {analyse.bedarf} Ausdrucken
          </strong>{" "}
          rechnen. Pakete, die das schaffen, beginnen bei{" "}
          <strong className="font-semibold text-slate-900">
            {euro(analyse.min)} €
          </strong>
          ; die Hälfte liegt unter{" "}
          <strong className="font-semibold text-slate-900">
            {euro(analyse.median)} €
          </strong>{" "}
          — rund {euroDezimal(analyse.proGast)} € pro Kopf und damit meist
          weniger als das Menü.
        </p>
        <p className="mt-4">
          Anders als bei privaten Feiern kommen alle Gäste in einem engen
          Zeitfenster: Zwischen Essen und Programm entstehen zwei Drittel der
          Bilder. Ein Paket mit{" "}
          <Link
            href="/fotobox-mieten-mit-sofortdruck"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Druck-Flatrate
          </Link>{" "}
          nimmt die Rechnerei heraus — {analyse.mitFlatrate} der passenden
          Fotoboxen bieten eine.
        </p>
      </Abschnitt>

      <Abschnitt
        id="weihnachtsfeier"
        titel="Fotobox für die Weihnachtsfeier"
        untertitel="Der Termin, an dem alle gleichzeitig suchen."
      >
        <p>
          Die Weihnachtsfeier ist der häufigste Anlass in diesem Bereich — und
          der mit dem engsten Markt. Zwischen dem ersten und dem dritten
          Adventswochenende feiern die meisten Unternehmen, und genau dann sind
          die beliebten Boxen vergeben. Wer im September bucht, hat die freie
          Wahl; wer Ende November anfragt, nimmt, was übrig ist.
        </p>
        <p className="mt-4">
          Praktischer Hinweis für Dezember-Termine: Versandboxen werden in der
          Vorweihnachtszeit von denselben Paketdiensten transportiert wie alles
          andere. Plant einen Tag Puffer ein, oder nehmt einen{" "}
          <Link
            href="/fotobox-mieten-in-der-naehe"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Anbieter aus der Region
          </Link>{" "}
          — {analyse.bauenAuf} der passenden Anbieter liefern und bauen selbst
          auf.
        </p>
      </Abschnitt>

      <Abschnitt
        id="branding"
        titel="Logo auf den Ausdrucken"
        untertitel="Der Unterschied zwischen Partyfoto und Firmenandenken."
      >
        <p>
          Ein eigenes Drucklayout mit Firmenlogo, Anlass und Datum macht aus dem
          Streifen ein Andenken, das am Monitor hängen bleibt — und bei einer
          Messe ein Werbemittel, das der Besucher freiwillig mitnimmt.{" "}
          <strong className="font-semibold text-slate-900">
            {analyse.mitEigenemLayout} der passenden Fotoboxen
          </strong>{" "}
          erlauben ein eigenes Layout, {analyse.mitLayout} bieten zumindest eine
          Auswahl an Vorlagen.
        </p>
        <p className="mt-4">
          Liefert die Vorlage rechtzeitig an — die meisten Anbieter brauchen sie
          einige Tage vorher, um sie einzurichten. Eine Druckdatei im
          Streifenformat mit Logo an einer Ecke reicht; große Flächen Firmenfarbe
          kosten Toner und wirken auf dem kleinen Format erdrückend.
        </p>
      </Abschnitt>

      <Abschnitt
        id="datenschutz"
        titel="Dürfen Fotos von Mitarbeitenden verwendet werden?"
        untertitel="Die Frage, an der interne Freigaben hängen."
      >
        <p>
          Für die Feier selbst ist das unkritisch: Wer sich in die Box stellt,
          tut das freiwillig, und der Ausdruck gehört ihm. Heikel wird es erst,
          wenn die Bilder danach weiterverwendet werden — im Intranet, auf der
          Karriereseite oder in sozialen Netzwerken.
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-5 marker:text-slate-400">
          <li>
            Für die Veröffentlichung braucht ihr die Einwilligung der
            Abgebildeten. Ein Hinweisschild neben der Box, das die spätere
            Verwendung nennt, ist das Mindeste.
          </li>
          <li>
            Klärt mit dem Anbieter, wie lange er die Bilder speichert und wann
            sie gelöscht werden. Bei{" "}
            {analyse.mitGalerie} der passenden Fotoboxen gibt es eine
            Online-Galerie — dann liegen die Bilder eine Zeit lang auf seinem
            Server.
          </li>
          <li>
            Bei Betriebsrat oder Datenschutzbeauftragtem lohnt eine kurze
            Vorabinfo. Das dauert einen Tag und verhindert die Diskussion
            hinterher.
          </li>
        </ul>
      </Abschnitt>

      <Abschnitt
        id="ablauf"
        titel="100 Gäste in zwei Stunden"
        untertitel="Warum Durchsatz bei Firmenfeiern zum Thema wird."
      >
        <p>
          Ein Ausdruck braucht rund zwölf Sekunden, eine Gruppe vor der Box
          zwei bis drei Minuten. Realistisch sind damit 40 bis 60 Bilder pro
          Stunde. Bei einer Firmenfeier drängen sich alle im selben Zeitfenster
          — vor der Box entsteht eine Schlange, die Leute weggehen lässt.
        </p>
        <p className="mt-4">Zwei Dinge helfen:</p>
        <ul className="mt-3 list-disc space-y-2 pl-5 marker:text-slate-400">
          <li>
            <strong className="font-semibold text-slate-900">
              Die Box früh öffnen
            </strong>{" "}
            — schon während des Empfangs, nicht erst nach den Reden. Das
            verteilt den Andrang über den Abend.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">
              Jemanden daneben stellen
            </strong>{" "}
            — bei Anbietern vor Ort oft buchbar. Diese Person legt Papier nach,
            sortiert die Schlange und spart am Ende mehr Zeit, als sie kostet.
          </li>
        </ul>
        <p className="mt-6 text-xs text-slate-500">
          Zahlen aus den {analyse.anzahlBoxen} Fotoboxen im Vergleich, Stand{" "}
          {stand}.
        </p>
      </Abschnitt>
    </>
  );
}
